/**
 * Page copy that is structural (repeated lists, numbered steps) rather than
 * one-off prose. One-off prose stays inline in its page component, where it
 * reads in context; anything rendered through a .map() lives here.
 */

export const philosophyPillars = [
  {
    num: '01',
    title: 'Personal, from the first sketch',
    body: 'No templates. We start with how you cook, gather, and unwind, then design a floor plan around your family instead of the other way around.',
  },
  {
    num: '02',
    title: 'Craftsmanship you can see',
    body: 'Board-and-batten siding, considered trim work, honest materials. Details that hold up to a close look, not just a drive-by.',
  },
  {
    num: '03',
    title: 'One point of contact, start to finish',
    body: 'You talk to the people actually building your home — not a rotating cast. Clear timelines, clear budgets, no surprises.',
  },
];

/** The condensed four-step summary shown on the home page. */
export const processSummary = [
  { num: '01', title: 'Consultation', body: 'We talk through your lot, budget, and how you want to live.' },
  { num: '02', title: 'Design', body: 'Custom floor plans and selections, refined until they fit.' },
  { num: '03', title: 'Building', body: 'Scheduled milestones with regular walkthroughs on site.' },
  { num: '04', title: 'Move-In', body: 'A final walkthrough, warranty details, and the keys are yours.' },
];

/** The full five-stage process. Named stages are a documented product fact. */
export const processStages = [
  {
    num: '01',
    title: 'Consultation',
    body: "We start with a conversation, not a sales pitch. We'll talk through your lot (or help you find one), your budget, your must-haves, and how your family actually uses a home day to day. You'll leave with a realistic sense of scope, timeline, and investment before anything is signed.",
    meta: 'Typical length: 1–2 weeks',
  },
  {
    num: '02',
    title: 'Design & Selections',
    body: 'Your floor plan is drawn around your life, not pulled from a catalog. We refine layout, elevations, and finishes together — siding, roofing, cabinetry, fixtures — until every selection feels intentional. You approve final plans and a fixed scope before we break ground.',
    meta: 'Typical length: 6–10 weeks',
  },
  {
    num: '03',
    title: 'Permitting & Pre-Construction',
    body: "We handle the paperwork: permitting through the City of Bloomington or McLean County, site engineering, and scheduling trades. You'll get a firm construction start date and a payment schedule tied to real milestones, not guesswork.",
    meta: 'Typical length: 3–6 weeks',
  },
  {
    num: '04',
    title: 'Construction',
    body: "Foundation, framing, mechanicals, and finish work move through scheduled milestones. You're invited to walk the site at each major stage — framing complete, rough-in, drywall, finishes — with a single point of contact for every question along the way.",
    meta: 'Typical length: 7–10 months',
  },
  {
    num: '05',
    title: 'Walkthrough & Move-In',
    body: "A detailed final walkthrough catches every last detail before we hand you the keys. We'll cover your warranty, maintenance guide, and who to call if you ever need us — because our relationship doesn't end at closing.",
    meta: 'Plus a 1-year workmanship warranty',
  },
];

export const values = [
  {
    num: '01',
    title: 'Honesty in the numbers',
    body: 'A fixed scope and a real budget before we break ground — and clear communication the moment anything changes.',
  },
  {
    num: '02',
    title: 'Quality craftsmanship',
    body: "We choose materials and trade partners we'd put in our own homes, and we hold every detail to that standard.",
  },
  {
    num: '03',
    title: 'A relationship, not a transaction',
    body: "Our name is on the warranty. We're still answering the phone long after the last walkthrough.",
  },
];

export const projectStages = [
  { value: 'exploring', label: 'Just exploring' },
  /* Selected automatically for anyone arriving from a project page, so a
     buyer asking about a house that already exists is not filed under
     "just exploring" alongside someone with no site and no timeline. */
  { value: 'listed-home', label: 'Interested in a listed home' },
  { value: 'have-lot', label: 'I have a lot / land' },
  { value: 'ready', label: 'Ready to start planning' },
  { value: 'renovation', label: 'Not custom build — renovation question' },
];
